/**
 * NURBS knot-vector operations — knot insertion (Boehm's algorithm), curve
 * splitting, and point inversion (parameter-for-point search).
 *
 * These are the mathematical primitives behind SPLINE TRIM/EXTEND (MASTERPLAN
 * N3): trimming a SPLINE target cuts the curve at an exact parametric location
 * via knot insertion + control-point truncation, never a flattened/approximated
 * curve. Reference: Piegl & Tiller, "The NURBS Book" §5.2 (knot insertion, "The
 * NURBS Book" algorithm A5.1) and §5.3/§5.5 (curve splitting via knot insertion
 * up to full multiplicity — the same technique `ezdxf.math` uses for its NURBS
 * `Curve` knot-insertion / decompose-to-Bezier helpers).
 *
 * ## Why knot insertion (not resampling)
 *
 * Per CLAUDE.md ПРИОРИТЕТ #1 a trimmed/extended SPLINE must remain a genuine,
 * correctly-parameterized NURBS — never a re-fit approximation. Knot insertion
 * is the EXACT operation for this: inserting a knot value `u*` never changes
 * the curve (same C(u) for every u); it only refines the control polygon so a
 * NEW control point coincides exactly with C(u*) once the local multiplicity of
 * `u*` reaches `degree`. At that point the control-point array can be split at
 * the shared point into two independent, fully-clamped NURBS curves — no
 * sampling, no fitting, no loss of fidelity on the retained portion.
 *
 * ## Rational curves
 *
 * All formulas here operate uniformly via homogeneous coordinates
 * `(w·x, w·y, w·z, w)` (weight defaults to 1 when absent), so a non-rational
 * curve (`weights` undefined) is just the `w≡1` special case of the SAME code
 * path — matching how `evaluateNurbs` already treats `weights?.[i] ?? 1`.
 */

import type { SplinePoint } from "./splineMath";
import { evaluateNurbs, evaluateNurbsAtSpan } from "./splineMath";

/**
 * Geometric tolerance for knot-value / parameter coincidence tests.
 *
 * Tightened from `1e-9` to `1e-10` (Codex P2, round 11): "parameter spacing
 * has no geometric minimum" — a NORMALISED/DXF-imported knot vector can
 * legitimately contain two DISTINCT neighbouring knots closer than the OLD
 * tolerance (e.g. `..., 1, 1+5e-10, 2, ...`). Treating them as the same knot
 * inflates `knotMultiplicity`'s count, skipping insertion that should have
 * happened and selecting the WRONG slice boundary — for a degree-2
 * unclamped end, EXTEND's clamping path (`ensureClampedAtGrowEnd`) then
 * retains an off-domain control point and alters the original curve. `1e-10`
 * still comfortably exceeds `findNurbsParameterAtPoint`'s own Gauss-Newton
 * convergence precision (~1e-13), so a genuine point-inversion-derived
 * near-duplicate (the case `snapToExistingKnot` exists FOR) is still
 * reliably caught — this tightens the boundary between "the same knot" and
 * "a distinct, merely nearby one"; no FIXED tolerance can be "arbitrarily-
 * tight-distinct-spacing-proof" without a fully relative/context-aware
 * scheme, out of scope for this fix.
 *
 * Exported so `trimExtendSplineTarget.ts`'s `ensureClampedAtGrowEnd` uses
 * the IDENTICAL value for its own "same knot" direct-scan, rather than a
 * separately-hardcoded literal that could silently drift out of sync.
 */
export const KNOT_EPS = 1e-10;

/**
 * Piegl & Tiller Algorithm A2.1 "FindSpan" — the knot-span index `i` such
 * that `u ∈ [knots[i], knots[i+1])`, clamped to `[degree, numControlPoints-1]`
 * so a parameter exactly at the curve's end resolves to the LAST valid span
 * (the standard convention that makes the final control point authoritative
 * at `u = uMax`).
 */
export function findKnotSpan(
  numControlPoints: number,
  degree: number,
  u: number,
  knots: number[],
): number {
  const n = numControlPoints - 1;
  const uMax = knots[n + 1] ?? 0;
  if (u >= uMax - KNOT_EPS) return n;
  const uMin = knots[degree] ?? 0;
  if (u <= uMin) return degree;

  let low = degree;
  let high = n + 1;
  let mid = Math.floor((low + high) / 2);
  // Safety cap: knots.length iterations is always enough for a correct binary
  // search; guards against a malformed (non-monotonic) knot vector looping
  // forever instead of just returning an imperfect-but-terminating answer.
  let guard = knots.length + 4;
  while (
    (u < (knots[mid] ?? 0) || u >= (knots[mid + 1] ?? Infinity)) &&
    guard-- > 0
  ) {
    if (u < (knots[mid] ?? 0)) high = mid;
    else low = mid;
    mid = Math.floor((low + high) / 2);
  }
  return mid;
}

/** Count how many entries of `knots` equal `u` within tolerance. */
export function knotMultiplicity(
  u: number,
  knots: number[],
  tol = KNOT_EPS,
): number {
  let count = 0;
  for (const k of knots) if (Math.abs(k - u) <= tol) count++;
  return count;
}

/**
 * Snap `u` to an EXISTING knot value within `tol`, if one exists. A
 * point-inversion-derived `u` (`findNurbsParameterAtPoint`, used to locate a
 * TRIM cut) can land within `KNOT_EPS` of an existing knot WITHOUT being
 * bit-identical to it — a routine outcome of numeric root-finding, not an
 * edge case. Left unsnapped, `splitNurbsCurve`'s multiplicity count mixes two
 * distinct-but-nearly-equal knot values into what should be ONE canonical
 * run, silently breaking its "reaches exactly `degree` copies" assumption
 * (Codex P2). Snapping bounds the introduced parameter-space error by `tol`
 * itself — geometrically negligible, matching every other tolerance-scale
 * "same point" convention in this package.
 */
function snapToExistingKnot(
  u: number,
  knots: number[],
  tol = KNOT_EPS,
): number {
  let nearest = u;
  let nearestDist = tol;
  for (const k of knots) {
    const d = Math.abs(k - u);
    if (d <= nearestDist) {
      nearest = k;
      nearestDist = d;
    }
  }
  return nearest;
}

type HomogeneousPoint = [number, number, number, number]; // wx, wy, wz, w

function toHomogeneous(p: SplinePoint, w: number): HomogeneousPoint {
  return [p.x * w, p.y * w, (p.z ?? 0) * w, w];
}

function fromHomogeneous(h: HomogeneousPoint): SplinePoint {
  const w = Math.abs(h[3]) > 1e-12 ? h[3] : 1;
  return { x: h[0] / w, y: h[1] / w, z: h[2] / w };
}

export type NurbsCurveData = {
  controlPoints: SplinePoint[];
  knots: number[];
  /** `undefined` ⇒ non-rational (every weight is implicitly 1). */
  weights: number[] | undefined;
};

/**
 * Boehm's algorithm — insert ONE copy of knot value `u` into the curve,
 * returning a new (never mutated in place) control-point / knot-vector pair
 * that represents the IDENTICAL curve, refined at `u`. Rational curves are
 * inserted in homogeneous coordinates (Piegl & Tiller §4.5 "NURBS knot
 * insertion via the 4-D B-spline").
 */
function insertKnotOnce(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  u: number,
): NurbsCurveData {
  const n = controlPoints.length - 1;
  const p = degree;
  const k = findKnotSpan(controlPoints.length, p, u, knots);
  const hp: HomogeneousPoint[] = controlPoints.map((c, i) =>
    toHomogeneous(c, weights?.[i] ?? 1),
  );

  const newH: HomogeneousPoint[] = new Array(n + 2);
  for (let i = 0; i <= k - p; i++) newH[i] = hp[i]!;
  for (let i = k + 1; i <= n + 1; i++) newH[i] = hp[i - 1]!;
  for (let i = k - p + 1; i <= k; i++) {
    const ui = knots[i] ?? 0;
    const uip = knots[i + p] ?? 0;
    const denom = uip - ui;
    const alpha = Math.abs(denom) > 1e-12 ? (u - ui) / denom : 0;
    const prev = hp[i - 1]!;
    const cur = hp[i]!;
    newH[i] = [
      alpha * cur[0] + (1 - alpha) * prev[0],
      alpha * cur[1] + (1 - alpha) * prev[1],
      alpha * cur[2] + (1 - alpha) * prev[2],
      alpha * cur[3] + (1 - alpha) * prev[3],
    ];
  }

  const newControlPoints = newH.map(fromHomogeneous);
  const newWeights = weights ? newH.map((h) => h[3]) : undefined;
  const newKnots = [...knots.slice(0, k + 1), u, ...knots.slice(k + 1)];
  return {
    controlPoints: newControlPoints,
    knots: newKnots,
    weights: newWeights,
  };
}

/**
 * Insert `times` copies of knot value `u` (default 1). Applying the
 * single-insertion step repeatedly is mathematically equivalent to (and far
 * simpler/safer to implement correctly than) Piegl & Tiller's batched A5.1 —
 * curve control-point counts in real drawings are small (tens, not thousands)
 * so the O(times) re-application cost is immaterial.
 */
export function insertKnot(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  u: number,
  times = 1,
): NurbsCurveData {
  let cur: NurbsCurveData = { controlPoints, knots, weights };
  for (let i = 0; i < times; i++) {
    cur = insertKnotOnce(cur.controlPoints, cur.knots, cur.weights, degree, u);
  }
  return cur;
}

export type SplitNurbsResult = { left: NurbsCurveData; right: NurbsCurveData };

/**
 * Split a NURBS curve at parameter `u` into two independent, fully-clamped
 * curves sharing exactly one coincident control point (the curve's actual
 * point at `u`). Returns `null` when `u` is not a genuine INTERIOR parameter
 * (at or past either true end — nothing to split).
 *
 * Algorithm (Piegl & Tiller §5.3 "Splitting" — insert `u` up to multiplicity
 * `degree`, which is the maximum valid multiplicity for a knot that is not
 * one of the curve's own two ends; at that multiplicity the curve is exactly
 * C⁰ there — position-continuous, no derivative relationship enforced — which
 * is precisely "two independent curves touching at a point", i.e. a TRIM
 * split. `ezdxf.math` decomposes a NURBS into Bezier segments with the same
 * up-to-multiplicity-`degree` knot insertion).
 */
export function splitNurbsCurve(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  uRaw: number,
): SplitNurbsResult | null {
  const uMin = knots[degree] ?? 0;
  const uMax = knots[knots.length - degree - 1] ?? 1;
  if (uRaw <= uMin + KNOT_EPS || uRaw >= uMax - KNOT_EPS) return null;

  // Snap FIRST — see `snapToExistingKnot`'s doc comment. Every computation
  // below (multiplicity, insertion, slicing) then operates on ONE canonical
  // value, never a point-inversion-derived near-duplicate of an existing knot.
  const u = snapToExistingKnot(uRaw, knots);

  const s = knotMultiplicity(u, knots);
  const r = degree - s;
  const refined =
    r > 0
      ? insertKnot(controlPoints, knots, weights, degree, u, r)
      : { controlPoints, knots, weights };

  // After refinement `u` has multiplicity `degree` — UNLESS it already had
  // MORE than `degree` copies before this function ran (`s > degree`, i.e.
  // exactly `degree+1`: a prior EXTEND's join, see `buildLinearExtension`'s
  // doc comment, "an interior knot of multiplicity degree+1"). That case
  // needs no insertion (`r <= 0` above already skips it) AND a DIFFERENT
  // slicing offset: the existing degree+1 run already provides a full clamp
  // for BOTH sides with no "shared" Boor point to duplicate — unconditionally
  // still appending one more `u` (as the `s <= degree` formula does) would
  // over-multiply the left run to `degree+2`, an invalid/non-canonical NURBS
  // with a zero-support control point (Codex P2).
  const sharedIdx =
    findKnotSpan(refined.controlPoints.length, degree, u, refined.knots) -
    degree;
  const j = sharedIdx + 1; // first index of the multiplicity-`degree` knot run
  const alreadyFullyClamped = s > degree;

  const leftControlPoints = refined.controlPoints.slice(
    0,
    alreadyFullyClamped ? sharedIdx : sharedIdx + 1,
  );
  const leftKnots = alreadyFullyClamped
    ? refined.knots.slice(0, j + degree)
    : [...refined.knots.slice(0, j + degree), u];
  // The RIGHT side always rebuilds a FRESH `degree+1`-copy clamp regardless of
  // regime — always correct whether or not insertion happened on the way here.
  const rightControlPoints = refined.controlPoints.slice(sharedIdx);
  const rightKnots = [
    ...new Array(degree + 1).fill(u),
    ...refined.knots.slice(j + degree),
  ];

  const leftWeights = refined.weights?.slice(
    0,
    alreadyFullyClamped ? sharedIdx : sharedIdx + 1,
  );
  const rightWeights = refined.weights?.slice(sharedIdx);

  return {
    left: {
      controlPoints: leftControlPoints,
      knots: leftKnots,
      weights: leftWeights,
    },
    right: {
      controlPoints: rightControlPoints,
      knots: rightKnots,
      weights: rightWeights,
    },
  };
}

/**
 * Every DISTINCT knot value strictly between `uMin` and `uMax`, in
 * ascending order, bracketed by `uMin`/`uMax` themselves — i.e. the full set
 * of parametric breakpoints partitioning `[uMin, uMax]` into its
 * non-degenerate spans (a B-spline span between two consecutive breakpoints
 * is algebraically a single degree-`degree` polynomial piece — Piegl &
 * Tiller §2.2). Exported as the ONE shared implementation of this scan
 * (Codex P1, round 13): both `findNurbsParameterAtPoint`'s coarse seed
 * search here AND `trimExtendGeometry.ts`'s (`command-engine`) tessellation
 * sampler need to enumerate spans WITHOUT skipping a narrow one — a single
 * hand-rolled copy per call site risks the two drifting out of sync (one
 * fixed, the other not), exactly the failure mode this round's finding
 * exposed for point inversion after round 11 already fixed the
 * tessellation side.
 */
export function nurbsKnotSpanBreakpoints(
  knots: number[],
  uMin: number,
  uMax: number,
): number[] {
  const breakpoints: number[] = [uMin];
  for (const k of knots) {
    if (k > uMin && k < uMax && k > breakpoints[breakpoints.length - 1]!) {
      breakpoints.push(k);
    }
  }
  breakpoints.push(uMax);
  return breakpoints;
}

/**
 * A bounded retry count for `findNurbsParameterAtPoint`'s own
 * excludeNear-collision safety net (see that function's doc comment) — a
 * genuinely un-resolvable case (three-or-more coincident branches, a
 * documented follow-up per `resolveSplineCutParams`'s own doc comment)
 * still terminates promptly rather than looping.
 */
const MAX_EXCLUDE_COLLISION_RETRIES = 3;

/**
 * Point inversion's own coarse-scan/Gauss-Newton core, WITHOUT the outer
 * excludeNear-collision retry wrapper `findNurbsParameterAtPoint` (below)
 * applies — split out so that wrapper can call this repeatedly with a
 * progressively expanded `excludeNear` set without re-deriving `uMin`/
 * `uMax`/`samples` on every retry.
 *
 * Returns `collidedWithExclude: true` when the FINAL converged `u` lands
 * within this function's OWN locally-scaled `excludeRadiusAt` of one of
 * the `excludeNear` values it was given — Codex P2 (local review, batch-5
 * follow-up round 2): the wrapper previously re-derived its OWN, wider
 * GLOBAL-only radius for this check, so a result the core already found
 * CORRECTLY (via the local-scaled seeding radius) could still get wrongly
 * flagged as "colliding" by the wrapper's mismatched, too-generous
 * tolerance — needlessly excluding a genuinely valid root and sending the
 * retry off to something unrelated. The core is the ONE place that knows
 * its own local radius; the wrapper now just acts on this flag instead of
 * recomputing a different one.
 */
function findNurbsParameterAtPointCore(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  point: { x: number; y: number },
  uMin: number,
  uMax: number,
  samples: number,
  excludeNear: number[],
  iterations: number,
): { u: number; collidedWithExclude: boolean } {
  const totalWidth = uMax - uMin;
  const breakpoints =
    totalWidth > 0 ? nurbsKnotSpanBreakpoints(knots, uMin, uMax) : [uMin, uMax];
  const lastKnot = knots[knots.length - 1] ?? uMax;
  const evaluatePoint =
    Number.isInteger(degree) &&
    degree >= 0 &&
    controlPoints.length > degree &&
    knots.length >= controlPoints.length + degree + 1
      ? (u: number) =>
          u === uMin ||
          u === uMax ||
          // `findKnotSpan` clamps this tolerance window to `n`. At a
          // non-clamped repeated uMax that span can have zero width, so use
          // the canonical all-basis evaluator for the valid left-hand limit.
          (uMax !== lastKnot && u >= uMax - KNOT_EPS) ||
          (u !== lastKnot && Math.abs(u - lastKnot) < 1e-9)
            ? evaluateNurbs(controlPoints, knots, weights, degree, u)
            : evaluateNurbsAtSpan(
                controlPoints,
                knots,
                weights,
                degree,
                u,
                findKnotSpan(controlPoints.length, degree, u, knots),
              )
      : (u: number) => evaluateNurbs(controlPoints, knots, weights, degree, u);

  // The DISTINCT-knot-span width containing `uVal` — moved up (from its
  // original position just before the refinement loop) so the exclusion-
  // radius scaling below AND the refinement step's own numeric-derivative
  // sizing further down share the SAME one-truth lookup, rather than two
  // independently hand-rolled copies risking drift.
  const spanWidthAt = (uVal: number): number => {
    if (breakpoints.length < 2) return totalWidth;
    let low = 0;
    let high = breakpoints.length - 1;
    while (high - low > 1) {
      const middle = Math.floor((low + high) / 2);
      if (uVal <= breakpoints[middle]!) high = middle;
      else low = middle;
    }
    return breakpoints[high]! - breakpoints[low]!;
  };

  // `excludeNear` skips the coarse-sample neighbourhood of an ALREADY-found
  // parameter — used by a caller resolving MULTIPLE points that share the
  // same coordinate (a self-intersecting curve crossed by one cutting edge
  // at its own crossing point reports that point twice; without this, both
  // occurrences independently converge to the SAME `u`, losing the second
  // curve branch — Codex P2, same class of gap as `trimExtendPolylineTarget
  // .ts`'s `resolveCutParams`).
  //
  // Codex P2 (local review, batch-5 follow-up): a single GLOBAL radius
  // derived from the domain's OVERALL width/sample density (2 coarse-grid
  // steps) can be many times WIDER than a genuinely narrow knot span — for
  // a self-intersection whose BOTH parameter roots are confined to that
  // same narrow span (a real, if extreme, DXF-imported nonuniform spline
  // scenario), the global radius swallowed the ENTIRE span around the
  // first-found root, so no coarse seed survived anywhere near the second,
  // genuinely distinct root either. Scale each excluded point's own radius
  // to a quarter of the knot span it actually sits in instead — never
  // WIDER than the global radius (so an ordinary wide-span exclusion is
  // unaffected, matching the original "2 coarse-grid steps" sizing), but
  // narrow enough to leave room for a second root confined to that SAME
  // tiny span to still get its own seed.
  const globalExcludeRadius = (totalWidth / samples) * 2;
  const excludeRadiusAt = (eu: number): number =>
    Math.min(globalExcludeRadius, spanWidthAt(eu) * 0.25);

  type SeedCandidate = { d: number; u: number };
  const seedCandidates: SeedCandidate[] = [];
  let bestU = uMin;
  let bestD = Infinity;
  // Shared by `considerSeed` (skip an excluded seed) and the FINAL
  // `collidedWithExclude` check below — a single one-truth predicate rather
  // than the same `excludeNear.some(...)` expression hand-duplicated twice.
  const isExcluded = (u: number): boolean =>
    excludeNear.some((eu) => Math.abs(u - eu) <= excludeRadiusAt(eu));
  const considerSeed = (u: number): void => {
    if (isExcluded(u)) return;
    const p = evaluatePoint(u);
    const d = (p.x - point.x) ** 2 + (p.y - point.y) ** 2;
    seedCandidates.push({ d, u });
    if (d < bestD) {
      bestD = d;
      bestU = u;
    }
  };
  // Codex P1, round 13: the OLD coarse scan was a FLAT uniform-in-u loop
  // (`i/samples`) over the WHOLE domain — the identical failure mode round
  // 11 already fixed for `splineToPoints`' tessellation (`trimExtendGeometry
  // .ts`, `command-engine`): a knot span narrower than
  // `(uMax-uMin)/samples` gets ZERO seed samples, even when the point being
  // inverted lies entirely inside it. Codex's own repro: degree 1, control
  // points `(0,0),(0,0),(100,100),(100,100)`, knots
  // `[0,0,0.5,0.500001,1,1]` — the ENTIRE (0,0)->(100,100) traversal is
  // compressed into the `0.000001`-wide `[0.5,0.500001]` span (the outer
  // spans are degenerate — both endpoints coincide); a 256-sample uniform
  // scan (spacing ~0.0039) never lands inside it, so the seed comes from a
  // FLAT region with no useful gradient and Gauss-Newton converges to an
  // arbitrary point in that flat region instead of the true answer. Sample
  // per DISTINCT interior knot span instead (`nurbsKnotSpanBreakpoints`,
  // shared with the tessellation sampler), with a total-count budget
  // (mirroring that module's own round-12 fix) so a many-knot-span spline
  // still costs a BOUNDED number of `evaluateNurbs` calls per inversion.
  // (`totalWidth`/`breakpoints` are computed earlier in this function now —
  // shared with the exclusion-radius scaling above.)
  const maxTotalSeedSamples = samples * 8;
  // Set when the CAPPED (subsampled-span) branch below is taken — signals
  // the post-refinement "also try `uMin` as an independent candidate" step
  // further down, which only applies to this branch (see its own doc
  // comment for why the `else` branch's identical-shaped `considerSeed
  // (uMin)` call does not need the same treatment).
  let usedCappedScan = false;
  if (totalWidth > 0) {
    const spanCount = breakpoints.length - 1;
    // Codex P2, round 14: the OLD `Math.max(2, ...)` outer floor NEVER
    // actually honoured `maxTotalSeedSamples` — for `spanCount` beyond the
    // budget, the loop below still visited EVERY span (>= 1 seed each), so
    // the total was still `>= spanCount`, genuinely UNBOUNDED. A
    // `spanCount=10,000` spline therefore cost >=10,000 `evaluateNurbs`
    // calls PER inversion (each itself scanning the full knot vector),
    // and TRIM/EXTEND call this per cut/pick point — quadratic in the
    // worst case. Same genuine-hard-cap fix as `splineToPoints`
    // (`trimExtendGeometry.ts`, round 13 finding 3): once `spanCount`
    // itself exceeds the budget, switch to visiting only `budget` many
    // spans via even index-based subsampling, capping the total
    // regardless of how large `spanCount` grows.
    if (spanCount > maxTotalSeedSamples) {
      usedCappedScan = true;
      // Codex P2 (GitHub review, round 23): this capped branch originally
      // sampled only span MIDPOINTS plus `uMax` — unlike the `else` branch
      // below, it never actually EVALUATED `uMin`. `bestU = uMin` / `bestD
      // = Infinity` only seed the variables; since `uMin` itself was never
      // passed through `considerSeed`, its TRUE distance to `point` was
      // never computed, so it could never win the `d < bestD` comparison
      // on its own — a spline whose true nearest point sits AT (or
      // immediately adjacent to) `uMin` could be missed entirely if some
      // unrelated, merely-nearby midpoint elsewhere in this many-span curve
      // happened to sample closer.
      //
      // Codex P2 (LOCAL review, round 24, follow-up on the fix above):
      // simply folding `considerSeed(uMin)` into this SAME pooled
      // comparison (as the `else` branch already does) has the OPPOSITE
      // failure mode here — because only the single overall pool winner
      // ever gets refined, `uMin` can win the RAW coarse-distance
      // comparison purely for being a nearby LOCAL minimum while an
      // interior midpoint with a WORSE raw distance actually sits in the
      // basin of the TRUE global minimum (e.g. a curve that happens to
      // cross exactly through `point` thousands of spans away) — pooling
      // uMin in would silently discard that genuinely-better interior
      // candidate. Do NOT seed `uMin` into the pool here; it is refined as
      // an independent SEPARATE candidate after the pool's own winner is
      // refined (below), and only replaces the pool's result when its own
      // refined distance is genuinely no worse — never by out-competing a
      // better candidate at the coarse-sampling stage.
      for (let k = 0; k < maxTotalSeedSamples; k++) {
        const j = Math.min(
          spanCount - 1,
          Math.floor((k * spanCount) / maxTotalSeedSamples),
        );
        const mid = (breakpoints[j]! + breakpoints[j + 1]!) / 2;
        considerSeed(mid);
      }
      considerSeed(uMax);
    } else {
      const stdDensity = samples / totalWidth;
      const idealMinSeedsPerSpan = 4;
      const minSeedsPerSpan = Math.max(
        2,
        Math.min(
          idealMinSeedsPerSpan,
          Math.floor(maxTotalSeedSamples / Math.max(spanCount, 1)),
        ),
      );
      considerSeed(uMin);
      for (let j = 0; j < spanCount; j++) {
        const t0 = breakpoints[j]!;
        const t1 = breakpoints[j + 1]!;
        const w = t1 - t0;
        const segCount = Math.max(
          minSeedsPerSpan - 1,
          Math.ceil(w * stdDensity),
        );
        for (let i = 1; i <= segCount; i++) {
          considerSeed(t0 + (w * i) / segCount);
        }
      }
    }
  } else {
    considerSeed(uMin);
  }

  // (`spanWidthAt` — a plain linear scan, breakpoint counts are small
  // enough in practice — is defined earlier in this function now, shared
  // with the exclusion-radius scaling above; this refinement loop's own
  // per-iteration `h` sizing below reuses that SAME one-truth lookup
  // rather than a second hand-rolled copy.)
  //
  // Extracted into a named function (round 24 — was an inline loop over
  // `bestU` only) so it can be called a SECOND time, from `uMin`
  // independently, for the capped-scan branch's own candidate comparison
  // below — a single one-truth Gauss-Newton step, never two hand-rolled
  // copies that could drift out of sync.
  const refine = (u0: number): number => {
    let u = u0;
    for (let iter = 0; iter < iterations; iter++) {
      const p0 = evaluatePoint(u);
      const fx = p0.x - point.x;
      const fy = p0.y - point.y;
      if (fx * fx + fy * fy < 1e-20) break;
      // Codex P1, round 13 (same fix, extended to the REFINEMENT step): the
      // OLD numeric-derivative step was a FLAT `totalWidth * 1e-6` — for
      // Codex's own repro (a `0.000001`-wide span, the SAME order of
      // magnitude as this step) `uPrev`/`uNext` land OUTSIDE the true local
      // polynomial piece, in an ADJACENT degenerate span, so the numeric
      // derivative is computed from the WRONG piece and Gauss-Newton cannot
      // converge precisely even once the seed already landed inside the
      // right span. Bound `h` by the LOCAL span width too — same fraction
      // (`1e-2`) `splineEndTangentStep` (`trimExtendGeometry.ts`,
      // `command-engine`, rounds 6/10) already uses for the identical
      // "don't let a numeric step leave the true local piece" concern,
      // recomputed every iteration since `u` can legitimately cross a span
      // boundary while converging.
      // Codex P2, round 14: the OLD flat absolute floor (`1e-9`) could
      // OVERRIDE the span-relative cap for a span narrower than that floor
      // itself — Codex's own repro: knots `[0,0,0.5,0.5000000005,1,1]`, span
      // width `5e-10` (< `1e-9`). `spanWidthAt(u) * 1e-2 = 5e-12`, but
      // `Math.max(5e-12, 1e-9) = 1e-9` — the FLOOR won, giving an `h` TWICE
      // as wide as the span itself, so `uPrev`/`uNext` landed back in the
      // adjacent degenerate span (the exact failure the round-13 fix was
      // meant to prevent). No independent absolute floor is actually needed
      // here — the `span < 1e-15` guard a few lines below ALREADY handles
      // complete degeneracy (an `h` so tiny `uPrev`/`uNext` coincide) by
      // gracefully breaking out of the loop rather than dividing by a
      // near-zero `span`, so `h` can be PURELY span-relative without ever
      // risking an unguarded division.
      const localSpan = spanWidthAt(u);
      const h = Math.min(totalWidth * 1e-6, localSpan * 1e-2);
      const uPrev = Math.max(uMin, u - h);
      const uNext = Math.min(uMax, u + h);
      const span = uNext - uPrev;
      if (span < 1e-15) break;
      const pPrev = evaluatePoint(uPrev);
      const pNext = evaluatePoint(uNext);
      const dx = (pNext.x - pPrev.x) / span;
      const dy = (pNext.y - pPrev.y) / span;
      const denom = dx * dx + dy * dy;
      if (denom < 1e-18) break;
      const step = (fx * dx + fy * dy) / denom;
      let uNew = u - step;
      uNew = Math.min(uMax, Math.max(uMin, uNew));
      if (Math.abs(uNew - u) < 1e-13) {
        u = uNew;
        break;
      }
      u = uNew;
    }
    return u;
  };

  // A single best RAW sample is insufficient: distinct basins in one
  // polynomial span can have the opposite ordering after refinement. Retain
  // every discrete local minimum from the bounded scan, refine each, and
  // compare final distances. Equal-distance plateaus must retain every seed:
  // distinct curve spans can have identical raw samples but different refined
  // minima. The candidate count remains bounded by the scan's fixed seed
  // budget.
  const localMinimumSeeds = seedCandidates
    .filter((candidate, index, all) => {
      const previous = all[index - 1];
      const next = all[index + 1];
      return (
        (previous === undefined || candidate.d <= previous.d) &&
        (next === undefined || candidate.d <= next.d)
      );
    })
    .map(({ u: seed }) => seed);
  const refinementSeeds = [
    ...(usedCappedScan && !isExcluded(uMin) ? [uMin] : []),
    ...(localMinimumSeeds.length > 0 ? localMinimumSeeds : [bestU]),
  ].filter((seed, index, all) => all.indexOf(seed) === index);
  let bestAllowed: SeedCandidate | undefined;
  let bestColliding: SeedCandidate | undefined;
  for (const seed of refinementSeeds) {
    const refinedU = refine(seed);
    const p = evaluatePoint(refinedU);
    const candidate = {
      d: (p.x - point.x) ** 2 + (p.y - point.y) ** 2,
      u: refinedU,
    };
    if (isExcluded(refinedU)) {
      if (bestColliding === undefined || candidate.d < bestColliding.d) {
        bestColliding = candidate;
      }
    } else if (bestAllowed === undefined || candidate.d < bestAllowed.d) {
      bestAllowed = candidate;
    }
  }
  const u = (bestAllowed ?? bestColliding ?? { d: Infinity, u: bestU }).u;
  const collidedWithExclude = isExcluded(u);
  return { u, collidedWithExclude };
}

/**
 * Point inversion — the parameter `u ∈ [uMin, uMax]` whose curve point is
 * nearest to `point` (2-D, XY-planar — matching the rest of the TRIM/EXTEND
 * module's convention). Standard two-phase approach (Piegl & Tiller §6.1):
 * a coarse uniform sample locates the right neighbourhood, then a Gauss-Newton
 * refinement (minimising ‖C(u) − point‖² using a NUMERIC derivative, matching
 * this package's existing `splineToCubicBezier` tangent-estimation convention
 * rather than deriving the analytic B-spline derivative recursion) converges
 * to sub-tolerance accuracy. Gauss-Newton (dropping the C″·(C−P) curvature
 * term of the full Newton iteration on `f(u)=C′(u)·(C(u)−P)`) is the standard,
 * numerically stable simplification for this class of least-squares root
 * finding — it is a numerical-method choice, not a CAD-fidelity shortcut: the
 * curve geometry itself (evaluated via the exact `evaluateNurbs`) is never
 * approximated.
 *
 * `opts.excludeNear` (used by `resolveSplineCutParams` for a self-
 * intersecting curve's own second branch — see `findNurbsParameterAtPointCore`'s
 * doc comment) steers coarse SEEDING away from an already-found parameter,
 * but Gauss-Newton refinement itself is NOT constrained to avoid it — for a
 * self-intersection, BOTH parameter roots are exact `d=0` global minima of
 * the SAME distance function, so refinement from ANY seed can still
 * converge back into the excluded root's own neighbourhood even after a
 * genuinely different, well-seeded starting point (Codex P2, local review,
 * batch-5 follow-up). If the CORE's own result lands within its own
 * exclusion radius of an already-excluded parameter anyway, retry with that
 * result ALSO added to the exclusion set — bounded to
 * `MAX_EXCLUDE_COLLISION_RETRIES` attempts so a genuinely un-resolvable
 * case (three-or-more coincident branches, a documented follow-up per
 * `resolveSplineCutParams`'s own doc comment) still terminates promptly
 * rather than looping, returning its best-effort last attempt instead.
 */
export function findNurbsParameterAtPoint(
  controlPoints: SplinePoint[],
  knots: number[],
  weights: number[] | undefined,
  degree: number,
  point: { x: number; y: number },
  opts: { samples?: number; iterations?: number; excludeNear?: number[] } = {},
): number {
  const uMin = knots[degree] ?? 0;
  const uMax = knots[knots.length - degree - 1] ?? 1;
  const samples = Math.max(2, opts.samples ?? 256);
  const iterations = opts.iterations ?? 20;
  const originalExcludeNear = opts.excludeNear ?? [];
  if (originalExcludeNear.length === 0) {
    return findNurbsParameterAtPointCore(
      controlPoints,
      knots,
      weights,
      degree,
      point,
      uMin,
      uMax,
      samples,
      [],
      iterations,
    ).u;
  }
  // Uses the CORE's own `collidedWithExclude` flag (computed with its own
  // locally-scaled `excludeRadiusAt`) rather than re-deriving a separate,
  // wider GLOBAL-only radius here — Codex P2 (local review, batch-5
  // follow-up round 2): a mismatched wrapper-side radius could wrongly
  // flag a result the core already found CORRECTLY as "colliding",
  // needlessly excluding a genuinely valid root (see the core's own doc
  // comment for the full failure mode).
  let excludeNear = originalExcludeNear;
  let result: { u: number; collidedWithExclude: boolean } = {
    u: uMin,
    collidedWithExclude: false,
  };
  for (let attempt = 0; attempt < MAX_EXCLUDE_COLLISION_RETRIES; attempt++) {
    result = findNurbsParameterAtPointCore(
      controlPoints,
      knots,
      weights,
      degree,
      point,
      uMin,
      uMax,
      samples,
      excludeNear,
      iterations,
    );
    if (!result.collidedWithExclude) return result.u;
    excludeNear = [...excludeNear, result.u];
  }
  return result.u;
}
